
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Image from '@/components/AppImage/AppImage';
import dayjs from 'dayjs';
import { useAuth } from '@/contexts/AuthContext';
import { log, error as logError } from '@/utils/logger';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_BOOKING_PREBOOK_SUCCEEDED,
  EVENT_BOOKING_PREBOOK_FAILED,
  EVENT_BOOKING_PAYMENT_FORM_LOADED,
} from '@/lib/analyticsEvents';
import type { Hotel, HotelRate, HotelDetailsResponse, HotelSearchPricing } from '@/services/nuiteeBookingService';
import { nuiteeBookingService } from '@/services/nuiteeBookingService';
import { parseLocalDate } from '@/utils/dateUtils';
import logoIcon from '@/assets/logo-orange.svg';
import SandboxBanner from '../SandboxBanner/SandboxBanner';
import HotelDetailSearchBar from './HotelDetailSearchBar';
import NuiteeTermsContent from './NuiteeTermsContent';
import './CombinedGuestPaymentForm.css';
import './HotelDetailView.css'; // For room details modal styles

/** Delay (ms) before checking for Stripe validation errors and reverting the button from "Processing..." */
const PAYMENT_VALIDATION_ERROR_CHECK_MS = 700;

/** Show "Still working…" message after this many ms while prebooking (backend can take up to ~92s) */
const PREBOOK_STILL_WORKING_MS = 18000;

interface GuestInfo {
  firstName: string;
  lastName: string;
  email: string;
}

interface Guest {
  occupancyNumber: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface CombinedGuestPaymentFormProps {
  hotel: Hotel;
  selectedRate: HotelRate & { roomName: string; offerId: string };
  checkIn: string;
  checkOut: string;
  adults: number;
  rooms: number;
  context?: {
    postId?: string;
    placeId?: string; // Google Place ID for clientReference
    locationId?: string; // Canonical locations._id for creator booking attribution
    postLocationId?: string; // PostLocation slot id when booking from a trip/list place
  };
  pricingInfo?: HotelSearchPricing | null;
  onPaymentComplete: () => void;
  onBack: () => void;
  onClose: () => void;
  onWiderSearch?: () => void;
  isWiderSearchLoading?: boolean;
}

interface TaxOrFeeItem {
  amount: number;
  currency: string;
  included: boolean;
  description?: string;
}

interface PrebookRoomRate {
  rateId?: string;
  commission?: Array<{ amount: number; currency: string }>;
  retailRate?: {
    total?: Array<{ amount: number; currency: string }>;
    suggestedSellingPrice?: Array<{ amount: number; currency: string }>;
    taxesAndFees?: TaxOrFeeItem[];
  };
  cancellationPolicies?: {
    refundableTag?: string;
    cancelPolicyInfos?: Array<{ cancelTime: string; amount: number; currency: string }>;
  };
}

interface PrebookRoomType {
  rates?: PrebookRoomRate[];
}

interface PrebookData {
  prebookId: string;
  transactionId: string;
  secretKey: string;
  offerId: string;
  hotelId: string;
  price?: number;
  currency?: string;
  roomTypes?: PrebookRoomType[];
  cancellationPolicies?: {
    refundableTag?: string;
    cancelPolicyInfos?: Array<{ cancelTime: string; amount: number; currency: string }>;
  };
}


type HotelRoomDetail = NonNullable<HotelDetailsResponse['data']['rooms']>[number];

interface LiteAPIPaymentConfig {
  publicKey: string;
  secretKey: string;
  returnUrl: string;
  targetElement: string;
  appearance?: { theme?: string };
  options?: { business?: { name?: string } };
}

interface LiteAPIPaymentInstance {
  handlePayment: () => void;
}

declare global {
  interface Window {
    LiteAPIPayment?: new (config: LiteAPIPaymentConfig) => LiteAPIPaymentInstance;
  }
}

// Format "2026-03-26 07:59:00" → "Mar 26, 2026"
function formatCancelDate(cancelTime: string): string {
  try {
    const d = new Date(cancelTime.replace(' ', 'T'));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return cancelTime;
  }
}

// Format "2026-03-26 07:59:00" → "07:59 AM"
function formatCancelTime(cancelTime: string): string {
  try {
    const d = new Date(cancelTime.replace(' ', 'T'));
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch {
    return '';
  }
}

const CombinedGuestPaymentForm: React.FC<CombinedGuestPaymentFormProps> = ({
  hotel,
  selectedRate,
  checkIn,
  checkOut,
  adults,
  rooms,
  context,
  pricingInfo = null,
  onPaymentComplete: _onPaymentComplete,
  onBack,
  onClose,
  onWiderSearch,
  isWiderSearchLoading = false,
}) => {
  const { user } = useAuth();
  const [holder, setHolder] = useState<GuestInfo>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [guests, setGuests] = useState<Guest[]>([]);
  const [isPrebooking, setIsPrebooking] = useState(false);
  const [prebookComplete, setPrebookComplete] = useState(false);
  const [prebookId, setPrebookId] = useState<string | null>(null);
  const [prebookData, setPrebookData] = useState<PrebookData | null>(null);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [publicKey, setPublicKey] = useState<'sandbox' | 'live'>('sandbox');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isPrebookError, setIsPrebookError] = useState(false);
  const [prebookStillWorking, setPrebookStillWorking] = useState(false);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [showRoomDetailsModal, setShowRoomDetailsModal] = useState(false);
  const [roomDetailsModalImageIndex, setRoomDetailsModalImageIndex] = useState(0);
  const [hotelDetails, setHotelDetails] = useState<HotelDetailsResponse | null>(null);
  const [showImportantInfoModal, setShowImportantInfoModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isSandboxMode, setIsSandboxMode] = useState<boolean | null>(null);
  
  const paymentContainerRef = useRef<HTMLDivElement>(null);
  const paymentInstanceRef = useRef<LiteAPIPaymentInstance | null>(null);
  const paymentObserverRef = useRef<MutationObserver | null>(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 10;
  const hasPrebookedRef = useRef(false);

  // Show "Still working…" after PREBOOK_STILL_WORKING_MS while prebooking
  useEffect(() => {
    if (!isPrebooking) {
      setPrebookStillWorking(false);
      return;
    }
    const timer = window.setTimeout(() => setPrebookStillWorking(true), PREBOOK_STILL_WORKING_MS);
    return () => window.clearTimeout(timer);
  }, [isPrebooking]);

  // Check if backend is in sandbox mode
  useEffect(() => {
    const checkEnvironment = async () => {
      try {
        const response = await nuiteeBookingService.getEnvironment();
        setIsSandboxMode(response.data.isSandbox);
        log('[CombinedGuestPaymentForm] Environment check complete', {
          isSandbox: response.data.isSandbox,
          environment: response.data.environment,
          sandboxBannerWillShow: response.data.isSandbox === true,
          note: 'sandbox banner uses /environment endpoint; Stripe key mode uses prebook response sandbox field — these are independent',
        });
      } catch (err) {
        logError('[CombinedGuestPaymentForm] Failed to check environment', err);
        // Default to null (no banner shown) if check fails
        setIsSandboxMode(null);
      }
    };

    checkEnvironment();
  }, []);

  // Run prebook (used on mount and on "Try again"); safe to call with same offerId
  const runPrebook = useCallback(async () => {
    log('[CombinedGuestPaymentForm] Prebooking', {
      offerId: selectedRate.offerId,
      hasContext: !!context,
      contextPostId: context?.postId,
    });

    localStorage.setItem('booking_params', JSON.stringify({ adults, rooms }));
    const contextToStore: Record<string, string> = {};
    if (context?.postId) contextToStore.postId = context.postId;
    if (context?.placeId) contextToStore.placeId = context.placeId;
    if (context?.locationId) contextToStore.locationId = context.locationId;
    if (context?.postLocationId) contextToStore.postLocationId = context.postLocationId;
    if (Object.keys(contextToStore).length > 0) {
      localStorage.setItem('booking_context', JSON.stringify(contextToStore));
    }

    setPaymentError(null);
    setIsPrebookError(false);
    setPrebookComplete(false);
    setPrebookId(null);
    setPrebookData(null);
    setTransactionId(null);
    setSecretKey(null);
    retryCountRef.current = 0;
    setIsPrebooking(true);
    try {
      const prebookResponse = await nuiteeBookingService.prebookRate({
        offerId: selectedRate.offerId,
      });

      const resolvedPublicKey = prebookResponse.sandbox === false ? 'live' : 'sandbox';
      log('[CombinedGuestPaymentForm] Prebook successful', {
        prebookId: prebookResponse.data.prebookId,
        hasTransactionId: !!prebookResponse.data.transactionId,
        hasSecretKey: !!prebookResponse.data.secretKey,
        sandboxRaw: prebookResponse.sandbox,
        resolvedPublicKey,
        hotelId: prebookResponse.data.hotelId,
      });

      if (prebookResponse.data.hotelId) {
        const existingContext = localStorage.getItem('booking_context');
        const ctx = existingContext ? JSON.parse(existingContext) : {};
        ctx.hotelId = prebookResponse.data.hotelId;
        localStorage.setItem('booking_context', JSON.stringify(ctx));
      }

      setPrebookId(prebookResponse.data.prebookId);
      setPrebookData(prebookResponse.data);
      setTransactionId(prebookResponse.data.transactionId);
      setSecretKey(prebookResponse.data.secretKey);
      setPublicKey(resolvedPublicKey);
      setPrebookComplete(true);
      WebAnalytics.track(EVENT_BOOKING_PREBOOK_SUCCEEDED, {
        hotel_id: hotel.hotelId,
        hotel_name: hotel.name,
        prebook_id: prebookResponse.data.prebookId,
        room_name: selectedRate.roomName,
        price: selectedRate.price,
        currency: selectedRate.currency,
        check_in: checkIn,
        check_out: checkOut,
      });
    } catch (err: unknown) {
      logError('[CombinedGuestPaymentForm] Prebook failed', err);
      setPaymentError('Failed to reserve your booking. Please try again.');
      setIsPrebookError(true);
      const errMessage = err instanceof Error ? err.message : 'Unknown error';
      const errCode = (err as Record<string, unknown>)?.code ?? (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code ?? null;
      WebAnalytics.track(EVENT_BOOKING_PREBOOK_FAILED, {
        hotel_id: hotel.hotelId,
        hotel_name: hotel.name,
        room_name: selectedRate.roomName,
        error_message: errMessage,
        error_code: errCode,
      });
    } finally {
      setIsPrebooking(false);
    }
  }, [selectedRate.offerId, adults, rooms, context, hotel.hotelId, hotel.name, selectedRate.roomName, selectedRate.price, selectedRate.currency, checkIn, checkOut]);

  // Trigger prebooking automatically on mount (once)
  useEffect(() => {
    if (hasPrebookedRef.current) return;
    hasPrebookedRef.current = true;
    runPrebook();
  }, [runPrebook]);

  // Fetch hotel details for room details modal
  useEffect(() => {
    const fetchHotelDetails = async () => {
      try {
        const details = await nuiteeBookingService.getHotelDetails(hotel.hotelId, 'en');
        setHotelDetails(details);
        log('[CombinedGuestPaymentForm] Hotel details fetched for room modal', {
          hotelId: hotel.hotelId,
          hasRooms: !!details.data?.rooms?.length,
          roomCount: details.data?.rooms?.length,
        });
      } catch (err) {
        logError('[CombinedGuestPaymentForm] Failed to fetch hotel details', err);
      }
    };
    fetchHotelDetails();
  }, [hotel.hotelId]);

  // Pre-fill from auth if logged in
  useEffect(() => {
    if (user) {
      let firstName = '';
      let lastName = '';
      
      if (user.first_name && user.last_name) {
        firstName = user.first_name as string;
        lastName = user.last_name as string;
      } else if (user.firstName && user.lastName) {
        firstName = user.firstName as string;
        lastName = user.lastName as string;
      } else if (user.name) {
        const nameParts = (user.name as string).trim().split(/\s+/);
        if (nameParts.length > 0) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ') || '';
        }
      }
      
      setHolder({
        firstName,
        lastName,
        email: user.email || '',
      });
    }
  }, [user]);

  // Initialize guests array
  useEffect(() => {
    const newGuests: Guest[] = Array(adults).fill(null).map((_, index) => {
      const guestsPerRoom = Math.ceil(adults / rooms);
      const occupancyNumber = Math.floor(index / guestsPerRoom) + 1;
      const existingGuest = guests[index];
      
      return {
        occupancyNumber,
        firstName: existingGuest?.firstName || (index === 0 ? holder.firstName : ''),
        lastName: existingGuest?.lastName || (index === 0 ? holder.lastName : ''),
        email: existingGuest?.email || (index === 0 ? holder.email : ''),
      };
    });
    setGuests(newGuests);
    // guests is set by this effect (adding it would cause infinite loop); holder fields are read as initial values only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adults, rooms]);

  // Pre-fill first guest with holder info
  useEffect(() => {
    if (holder.firstName && holder.lastName && holder.email && guests.length > 0 && !guests[0].firstName) {
      setGuests((prev) => {
        const newGuests = [...prev];
        if (newGuests[0]) {
          newGuests[0] = {
            ...newGuests[0],
            firstName: holder.firstName,
            lastName: holder.lastName,
            email: holder.email,
          };
        }
        return newGuests;
      });
    }
    // guests array is set by this effect; using guests.length in deps is intentional to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holder, guests.length]);

  const handleHolderChange = useCallback((field: keyof GuestInfo, value: string) => {
    setHolder((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleGuestChange = useCallback((index: number, field: 'firstName' | 'lastName' | 'email', value: string) => {
    setGuests((prev) => {
      const newGuests = [...prev];
      newGuests[index] = {
        ...newGuests[index],
        [field]: value,
      };
      return newGuests;
    });
  }, []);

  // Store guest info in localStorage whenever it changes (for booking completion page)
  useEffect(() => {
    if (holder.firstName && holder.lastName && holder.email) {
      localStorage.setItem('booking_holder', JSON.stringify(holder));
    }
  }, [holder]);

  useEffect(() => {
    const validGuests = guests.filter((g) => g.firstName && g.lastName && g.email);
    if (validGuests.length > 0) {
      localStorage.setItem('booking_guests', JSON.stringify(validGuests));
    }
  }, [guests]);

  // Function to customize the payment button after SDK renders
  const customizePaymentButton = (container: HTMLDivElement | null) => {
    if (!container) return;

    // Find the submit button in the payment form
    const findAndCustomizeButton = (): boolean => {
      const buttons = container.querySelectorAll('button');
      
      // Find the submit/pay button using Array.from to avoid forEach closure issues
      const buttonsArray = Array.from(buttons);
      const submitButton = buttonsArray.find((btn) => {
        const text = btn.textContent?.toLowerCase() || '';
        return text.includes('pay') || btn.type === 'submit';
      });

      if (!submitButton) {
        return false;
      }

      const completeBookingHtml = 'Complete Booking <span style="margin-left: 4px;">›</span>';
      const processingHtml = `
        <span style="display: inline-flex; align-items: center; gap: 8px;">
          <span class="payment-button-spinner" style="
            width: 20px;
            height: 20px;
            border: 2px solid rgba(255,255,255,0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          "></span>
          Processing...
        </span>
      `;
      submitButton.innerHTML = completeBookingHtml;

      // Show spinner only when submission actually proceeds; revert if Stripe shows validation errors
      submitButton.addEventListener('click', () => {
        if (submitButton.disabled) return;
        submitButton.innerHTML = processingHtml;
        setTimeout(() => {
          if (!container.isConnected || !submitButton.isConnected) return;
          const errEl = container.querySelector('#st-error-message');
          const hasError = (errEl?.textContent?.trim().length ?? 0) > 0;
          if (hasError && !submitButton.disabled) {
            submitButton.innerHTML = completeBookingHtml;
          }
        }, PAYMENT_VALIDATION_ERROR_CHECK_MS);
      }, { once: false });

      log('[CombinedGuestPaymentForm] Payment button customized');
      return true;
    };

    // Try immediately and with retries (SDK might render button async)
    if (!findAndCustomizeButton()) {
      let attempts = 0;
      const maxAttempts = 10;
      const interval = setInterval(() => {
        attempts++;
        if (findAndCustomizeButton() || attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 500);
    }
  };

  // Load payment SDK when prebook is complete
  useEffect(() => {
    if (!prebookComplete || !transactionId || !secretKey) return;

    const loadPaymentSDK = () => {
      if (window.LiteAPIPayment) {
        log('[CombinedGuestPaymentForm] Payment SDK already loaded');
        initializePayment();
        return;
      }

      log('[CombinedGuestPaymentForm] Loading Payment SDK');
      setIsLoadingPayment(true);

      const script = document.createElement('script');
      script.src = 'https://payment-wrapper.liteapi.travel/dist/liteAPIPayment.js?v=a1';
      script.async = true;
      
      script.onload = () => {
        if (!window.LiteAPIPayment) {
          logError('[CombinedGuestPaymentForm] Payment SDK script loaded but LiteAPIPayment not available');
          setPaymentError('Payment SDK failed to load. Please refresh and try again.');
          setIsLoadingPayment(false);
          return;
        }
        log('[CombinedGuestPaymentForm] Payment SDK loaded successfully');
        initializePayment();
      };

      script.onerror = () => {
        logError('[CombinedGuestPaymentForm] Failed to load Payment SDK');
        setPaymentError('Failed to load payment system. Please try again.');
        setIsLoadingPayment(false);
      };

      document.body.appendChild(script);
    };

    const initializePayment = () => {
      if (!paymentContainerRef.current) {
        retryCountRef.current += 1;
        if (retryCountRef.current >= MAX_RETRIES) {
          logError('[CombinedGuestPaymentForm] Container not ready after max retries');
          setPaymentError('Payment container not available. Please refresh and try again.');
          setIsLoadingPayment(false);
          return;
        }
        setTimeout(() => initializePayment(), 200);
        return;
      }

      if (!window.LiteAPIPayment) {
        logError('[CombinedGuestPaymentForm] Payment SDK not available');
        setPaymentError('Payment system not available. Please try again.');
        setIsLoadingPayment(false);
        return;
      }

      try {
        const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
        const returnUrl = prebookId && transactionId 
          ? `${baseUrl}/booking/complete?prebookId=${prebookId}&transactionId=${transactionId}`
          : `${baseUrl}/booking/complete`;
        
        const config = {
          publicKey,
          secretKey,
          returnUrl,
          targetElement: '#payment-container-combined',
          appearance: {
            theme: 'flat',
          },
          options: {
            business: {
              name: 'Globe Thrivers',
            },
          },
        };

        log('[CombinedGuestPaymentForm] Initialising LiteAPIPayment SDK', {
          publicKey,
          stripeKeyMode: publicKey === 'live' ? 'pk_live_... (live mode)' : 'pk_test_... (test mode)',
          secretKeyPrefix: secretKey ? secretKey.substring(0, 15) + '...' : null,
          returnUrl,
          isSandboxBannerShown: isSandboxMode,
          sandboxBannerMatchesPublicKey: isSandboxMode === (publicKey === 'sandbox'),
        });

        paymentInstanceRef.current = new window.LiteAPIPayment(config);
        paymentInstanceRef.current.handlePayment();
        WebAnalytics.track(EVENT_BOOKING_PAYMENT_FORM_LOADED, {
          hotel_id: hotel.hotelId,
          hotel_name: hotel.name,
          prebook_id: prebookId,
        });
        
        setTimeout(() => {
          const container = paymentContainerRef.current;
          const hasContent = container && (
            container.children.length > 0 ||
            container.innerHTML.trim().length > 0 ||
            container.textContent?.trim().length > 0
          );
          
          setIsLoadingPayment(false);
          
          if (!hasContent) {
            logError('[CombinedGuestPaymentForm] Payment form initialized but no content rendered');
            setPaymentError('Payment could not be initialised. Please go back and select your room again. If problem persists, please contact support.');
          } else {
            log('[CombinedGuestPaymentForm] Payment form initialized successfully');
            customizePaymentButton(container);

            // Stripe renders card fields inside iframes. When a loaderror fires (e.g.
            // key mode mismatch), Stripe removes those iframes but leaves the submit
            // button, so the container is never empty. Watch for iframe removal instead.
            const triggerPaymentLoadError = () => {
              logError('[CombinedGuestPaymentForm] Stripe iframes removed after initialization — payment load error');
              setPaymentError('Payment could not be initialised. Please go back and select your room again. If problem persists, please contact support.');
              paymentObserverRef.current?.disconnect();
            };

            if (typeof MutationObserver !== 'undefined' && container) {
              paymentObserverRef.current?.disconnect();
              paymentObserverRef.current = new MutationObserver(() => {
                const hasStripeInput = container.querySelector('iframe') !== null;
                if (!hasStripeInput) {
                  triggerPaymentLoadError();
                }
              });
              paymentObserverRef.current.observe(container, { childList: true, subtree: true });
            }

            // Fallback: if no Stripe iframe present after 4 s, assume load failed
            setTimeout(() => {
              if (!paymentContainerRef.current) return;
              const hasStripeInput = paymentContainerRef.current.querySelector('iframe') !== null;
              if (!hasStripeInput) {
                triggerPaymentLoadError();
              }
            }, 4000);
          }
        }, 1500);
      } catch (err) {
        logError('[CombinedGuestPaymentForm] Failed to initialize payment', err);
        setPaymentError(`Failed to initialize payment: ${err instanceof Error ? err.message : 'Unknown error'}. Please try again.`);
        setIsLoadingPayment(false);
      }
    };

    const timer = setTimeout(() => {
      if (paymentContainerRef.current) {
        loadPaymentSDK();
      } else {
        const retryTimer = setTimeout(() => {
          if (paymentContainerRef.current) {
            loadPaymentSDK();
          } else {
            logError('[CombinedGuestPaymentForm] Container still not ready after retry');
            setPaymentError('Payment container not available. Please refresh and try again.');
            setIsLoadingPayment(false);
          }
        }, 100);
        return () => clearTimeout(retryTimer);
      }
    }, 50);

    return () => {
      clearTimeout(timer);
      paymentObserverRef.current?.disconnect();
    };
    // hotel.hotelId, hotel.name, and isSandboxMode are only used for analytics/logging; adding them would re-initialize the payment SDK unnecessarily
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prebookComplete, prebookId, transactionId, secretKey, publicKey]);

  // Currency code to prefix mapping (for whitelabel-style display)
  const getCurrencyPrefix = (currency: string): string => {
    const currencyPrefixMap: Record<string, string> = {
      USD: 'US',
      CAD: 'CA',
      EUR: 'EU',
      GBP: 'GB',
      AUD: 'AU',
      NZD: 'NZ',
      JPY: 'JP',
      CNY: 'CN',
      HKD: 'HK',
      SGD: 'SG',
      CHF: 'CH',
      SEK: 'SE',
      NOK: 'NO',
      DKK: 'DK',
      PLN: 'PL',
      CZK: 'CZ',
      HUF: 'HU',
      BRL: 'BR',
      MXN: 'MX',
      INR: 'IN',
      KRW: 'KR',
      THB: 'TH',
      MYR: 'MY',
      IDR: 'ID',
      PHP: 'PH',
      VND: 'VN',
      ZAR: 'ZA',
      TRY: 'TR',
      RUB: 'RU',
      ILS: 'IL',
      AED: 'AE',
      SAR: 'SA',
    };
    return currencyPrefixMap[currency.toUpperCase()] || currency.toUpperCase();
  };

  // Format price with currency prefix (e.g., "US$760" instead of "$760")
  /**
   * Format price with currency symbol and optional decimal places
   * 
   * @param price - Numeric price value
   * @param currency - ISO currency code (e.g., 'USD')
   * @param decimals - Number of decimal places (default: 2 for exact pricing)
   * @returns Formatted price string (e.g., "US$223.16")
   * 
   * Payment page uses decimals=2 for exact pricing matching whitelabel
   */
  const formatPrice = (price: number, currency: string, decimals: number = 2) => {
    const prefix = getCurrencyPrefix(currency);
    
    // Format the price with specified decimal places
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(price);
    
    // Extract currency symbol to add country prefix
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(price);
    
    const currencySymbol = parts.find(part => part.type === 'currency')?.value || '$';
    
    // Add country prefix to currency symbol (e.g., "$223.16" -> "US$223.16")
    return formatted.replace(currencySymbol, `${prefix}${currencySymbol}`);
  };

  // Calculate nights
  const checkInDate = parseLocalDate(checkIn) || new Date();
  const checkOutDate = parseLocalDate(checkOut) || new Date();
  const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));

  // Format dates for display
  const formatDate = (dateStr: string) => {
    const date = parseLocalDate(dateStr) || new Date();
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  /**
   * Calculate payment breakdown from prebook response
   * Matches Nuitee whitelabel pricing display format
   * 
   * Price structure:
   * - roomBase: Base room price (before taxes/fees)
   * - includedTaxes: Taxes + commission already in "Pay now" amount
   * - localFees: Fees paid at property (excluded taxes)
   * - payNow: retailRate.total (amount charged upfront)
   * - payAtProperty: localFees (charged at hotel)
   * - grandTotal: payNow + payAtProperty
   * 
   * Key insight: Commission is treated as part of "included taxes" 
   * to correctly calculate room base that matches whitelabel display
   */
  const calculatePaymentBreakdown = useMemo(() => {
    // Log prebook data when available
    if (prebookData) {
      const rate = prebookData.roomTypes?.[0]?.rates?.[0];
      const taxesAndFees = rate?.retailRate?.taxesAndFees || [];
      const retailRate = rate?.retailRate || {};
      log('[CombinedGuestPaymentForm] Full retailRate keys:', Object.keys(retailRate));
      log('[CombinedGuestPaymentForm] retailRate.total:', JSON.stringify(retailRate.total));
      log('[CombinedGuestPaymentForm] retailRate.suggestedSellingPrice:', JSON.stringify(retailRate.suggestedSellingPrice));
      log('[CombinedGuestPaymentForm] rate.commission:', JSON.stringify(rate?.commission));
      log('[CombinedGuestPaymentForm] Taxes:', JSON.stringify(taxesAndFees));
      
      // Verification logging: Confirms correct includedTaxes calculation
      const total = retailRate.total?.[0]?.amount || 0;
      const targetRoomBase = 446.32;
      const impliedIncludedTaxes = total - targetRoomBase;
      log('[CombinedGuestPaymentForm] To get roomBase=446.32, includedTaxes should be:', impliedIncludedTaxes);
    }

    // Fallback during loading: Use search response data with ceiling rounding
    if (!prebookData) {
      return {
        perNight: Math.ceil(selectedRate.price / nights),
        roomBase: Math.ceil(selectedRate.price),
        includedTaxes: 0,
        localFees: 0,
        grandTotal: Math.ceil(selectedRate.price),
        payNow: Math.ceil(selectedRate.price),
        payAtProperty: 0,
      };
    }

    // Extract rate data from prebook response
    // Structure: data.roomTypes[0].rates[0].retailRate
    const rate = prebookData.roomTypes?.[0]?.rates?.[0];
    if (!rate?.retailRate) {
      // Fallback if prebook response is missing expected structure
      return {
        perNight: Math.ceil(selectedRate.price / nights),
        roomBase: Math.ceil(selectedRate.price),
        includedTaxes: 0,
        localFees: 0,
        grandTotal: Math.ceil(selectedRate.price),
        payNow: Math.ceil(selectedRate.price),
        payAtProperty: 0,
      };
    }

    // Extract core pricing values from prebook response
    const total = rate.retailRate.total?.[0]?.amount || 0;  // Amount charged upfront ("Pay now")
    const taxesAndFees = rate.retailRate.taxesAndFees || [];
    const commission = rate.commission?.[0]?.amount || 0;   // Platform commission

    // Sum taxes marked as included:true (already in total amount)
    let includedTaxes = taxesAndFees
      .filter((item: TaxOrFeeItem) => item.included === true)
      .reduce((sum: number, item: TaxOrFeeItem) => sum + (Number(item.amount) || 0), 0);

    // Add commission to included taxes
    // Whitelabel treats commission as part of "included taxes and fees"
    // This ensures: roomBase = total - (taxes + commission)
    // Example: 547.51 - (70.20 + 30.99) = 446.32
    includedTaxes += commission;

    // Sum taxes marked as included:false (pay at property)
    // These are "Local fees" displayed separately
    const excludedTaxes = taxesAndFees
      .filter((item: TaxOrFeeItem) => item.included === false)
      .reduce((sum: number, item: TaxOrFeeItem) => sum + (Number(item.amount) || 0), 0);

    // Calculate room base: total minus all included costs
    // This represents the pure room price before taxes/fees/commission
    const roomBase = total - includedTaxes;
    const perNight = roomBase / nights;

    // Calculate grand total: everything the customer pays
    const grandTotal = total + excludedTaxes;

    // Round all values to 2 decimal places for exact price display
    // Uses Math.round(x * 100) / 100 to ensure proper rounding (not ceiling)
    return {
      perNight: Math.round(perNight * 100) / 100,
      roomBase: Math.round(roomBase * 100) / 100,
      includedTaxes: Math.round(includedTaxes * 100) / 100,
      localFees: Math.round(excludedTaxes * 100) / 100,
      grandTotal: Math.round(grandTotal * 100) / 100,
      payNow: Math.round(total * 100) / 100,
      payAtProperty: Math.round(excludedTaxes * 100) / 100,
    };
  }, [prebookData, nights, selectedRate.price]);

  // Helper function to categorize amenities
  const categorizeAmenity = (amenity: string): 'internet' | 'services' | 'popular' | 'general' => {
    const lower = amenity.toLowerCase();
    if (lower.includes('wifi') || lower.includes('internet') || lower.includes('ethernet')) {
      return 'internet';
    }
    if (lower.includes('room service') || lower.includes('housekeeping') || lower.includes('concierge') || lower.includes('laundry')) {
      return 'services';
    }
    if (lower.includes('air condition') || lower.includes('tv') || lower.includes('television') || lower.includes('mini bar') || lower.includes('safe') || lower.includes('coffee') || lower.includes('tea')) {
      return 'popular';
    }
    return 'general';
  };

  // Prepare room details modal data
  const roomDetailsModalData = useMemo(() => {
    if (!showRoomDetailsModal || !hotelDetails?.data) {
      log('[CombinedGuestPaymentForm] Room details modal: missing data', {
        showRoomDetailsModal,
        hasHotelDetails: !!hotelDetails,
        hasData: !!hotelDetails?.data,
      });
      return null;
    }

    // Find room details from hotel details using mappedRoomId
    log('[CombinedGuestPaymentForm] Looking for room with mappedRoomId', {
      mappedRoomId: selectedRate.mappedRoomId,
      availableRooms: hotelDetails.data.rooms?.map((r: HotelRoomDetail) => ({ id: r.id, name: r.roomName })),
    });

    const roomDetailsData = hotelDetails.data.rooms?.find((r: HotelRoomDetail) => r.id === selectedRate.mappedRoomId);
    
    // Get room images - try multiple sources with fallback to hotel images
    let roomImages: string[] = [];
    if (selectedRate.roomImages && selectedRate.roomImages.length > 0) {
      roomImages = selectedRate.roomImages;
    } else if (roomDetailsData?.photos && roomDetailsData.photos.length > 0) {
      roomImages = roomDetailsData.photos.map((p) => p.url || '');
    } else if (hotelDetails.data.hotelImages && hotelDetails.data.hotelImages.length > 0) {
      // Fallback to hotel images if no room images available
      roomImages = hotelDetails.data.hotelImages.map((img) => img.url || '');
    } else if (hotel.images && hotel.images.length > 0) {
      // Final fallback to hotel prop images
      roomImages = hotel.images;
    }

    log('[CombinedGuestPaymentForm] Room details data found', {
      found: !!roomDetailsData,
      roomImagesCount: roomImages.length,
      roomImagesSource: selectedRate.roomImages?.length ? 'selectedRate' : 
                        roomDetailsData?.photos?.length ? 'roomDetailsData' : 
                        hotelDetails.data.hotelImages?.length ? 'hotelImages' : 
                        hotel.images?.length ? 'hotelProp' : 'none',
      hasAmenities: !!roomDetailsData?.roomAmenities?.length,
      hasDescription: !!roomDetailsData?.description,
      roomSize: roomDetailsData?.roomSizeSquare,
    });

    // Use hotel-level facilities for "Popular amenities" section to match whitelabel
    // Whitelabel shows hotel facilities (Restaurant, Room service, Terrace, etc.)
    // not room-specific amenities (Telephone, Air conditioning, etc.)
    const hotelFacilitiesList = hotelDetails.data.hotelFacilities || [];

    // Get amenities (keep for room-specific sections if needed)
    const rawAmenities = roomDetailsData?.roomAmenities || [];
    const amenityStrings = rawAmenities.map((a) => {
      const amenityObj = a as Record<string, unknown>;
      return (amenityObj.name || amenityObj.amenity || amenityObj.title || amenityObj.description || amenityObj.id || '') as string;
    }).filter((a): a is string => typeof a === 'string' && a.length > 0);

    // Categorize room amenities (for room-specific sections)
    const generalAmenities: string[] = [];
    const internetAmenities: string[] = [];
    const servicesAmenities: string[] = [];

    amenityStrings.forEach((amenity) => {
      const category = categorizeAmenity(amenity);
      switch (category) {
        case 'internet':
          internetAmenities.push(amenity);
          break;
        case 'services':
          servicesAmenities.push(amenity);
          break;
        default:
          generalAmenities.push(amenity);
      }
    });
    
    // Popular amenities: Use first 8 hotel facilities to match whitelabel display
    const popularAmenities = hotelFacilitiesList.slice(0, 8);

    // Build room size and occupancy strings
    const roomSize = roomDetailsData?.roomSizeSquare
      ? `${roomDetailsData.roomSizeSquare} ${roomDetailsData.roomSizeUnit || 'sqm'}`
      : null;
    const occupancy = roomDetailsData?.maxOccupancy || selectedRate.adultCount || adults;

    // Log room details modal data
    log('[CombinedGuestPaymentForm] Room details modal final data', {
      roomSize,
      occupancy,
      hotelFacilitiesCount: hotelFacilitiesList.length,
      popularAmenitiesCount: popularAmenities.length,
      popularAmenities: popularAmenities,
      generalAmenitiesCount: generalAmenities.length,
      description: roomDetailsData?.description?.substring(0, 50),
    });

    return {
      roomImages,
      roomDetailsData,
      generalAmenities,
      internetAmenities,
      servicesAmenities,
      popularAmenities,
      roomSize,
      occupancy,
    };
  }, [showRoomDetailsModal, hotelDetails, selectedRate, adults, hotel.images]);

  const locationLabel = useMemo(() => {
    const parts = [];
    if (hotel.city) parts.push(hotel.city);
    if (hotel.country) parts.push(hotel.country.toUpperCase());
    return parts.join(', ') || 'Location';
  }, [hotel.city, hotel.country]);

  const editCheckIn = useMemo(() => checkIn ? dayjs(checkIn) : null, [checkIn]);
  const editCheckOut = useMemo(() => checkOut ? dayjs(checkOut) : null, [checkOut]);

  // Extract cancellation policy from prebook response per backend spec
  const cancellationPolicy = useMemo((): { isRefundable: boolean; tiers: Array<{ cancelTime: string; amount: number; currency: string }> } | null => {
    const cp = prebookData?.cancellationPolicies ?? prebookData?.roomTypes?.[0]?.rates?.[0]?.cancellationPolicies;
    if (!cp) return null;

    const isRefundable = cp.refundableTag === 'RFN';
    const rawTiers: Array<{ cancelTime: string; amount: number; currency: string }> =
      Array.isArray(cp.cancelPolicyInfos) ? cp.cancelPolicyInfos : [];

    // The API only returns tiers where a fee kicks in (no explicit free tier).
    // When the booking is refundable and the first real tier has amount > 0,
    // synthesize a "free cancellation" entry: deadline = first tier's time minus 1 minute,
    // matching how the whitelabel derives the free period display.
    const tiers = [...rawTiers];
    if (isRefundable && tiers.length > 0 && tiers[0].amount !== 0) {
      try {
        const firstDeadline = new Date(tiers[0].cancelTime.replace(' ', 'T'));
        firstDeadline.setMinutes(firstDeadline.getMinutes() - 1);
        const pad = (n: number) => String(n).padStart(2, '0');
        const freeCancelTime = `${firstDeadline.getFullYear()}-${pad(firstDeadline.getMonth() + 1)}-${pad(firstDeadline.getDate())} ${pad(firstDeadline.getHours())}:${pad(firstDeadline.getMinutes())}:00`;
        tiers.unshift({ cancelTime: freeCancelTime, amount: 0, currency: tiers[0].currency });
      } catch {
        // If date parsing fails, skip the synthetic free tier
      }
    }

    return { isRefundable, tiers };
  }, [prebookData]);

  const totalBookingPrice = prebookData?.price ?? calculatePaymentBreakdown.grandTotal;

  return (
    <div className="combined-guest-payment-form">
      <div className="combined-guest-payment-header">
        <button onClick={onBack} className="combined-guest-payment-back" aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 16L6 10L12 4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="combined-guest-payment-header-logo" aria-hidden>
          <Image
            src={logoIcon}
            alt="Globe Thrivers"
            width={100}
            height={28}
            style={{ objectFit: 'contain' }}
          />
        </div>

        {/* Search pill — read-only display of location/dates/guests with wider-search button */}
        <HotelDetailSearchBar
          variant="header"
          editCheckIn={editCheckIn}
          editCheckOut={editCheckOut}
          editAdults={adults}
          editRooms={1}
          onCheckInChange={() => {}}
          onCheckOutChange={() => {}}
          onRoomsGuestsClick={() => {}}
          onChangeSearch={() => {}}
          locationLabel={locationLabel}
          onWiderSearch={onWiderSearch}
          isWiderSearchLoading={isWiderSearchLoading}
        />

        <button
          onClick={onClose}
          className="combined-guest-payment-close"
          aria-label="Close"
        >
          ×
        </button>
        
        {/* Sandbox Mode Banner - shown below header buttons when in sandbox mode */}
        {isSandboxMode === true && (
          <div className="combined-guest-payment-header-banner">
            <SandboxBanner />
          </div>
        )}

        {/* Pricing Banner - matches rate step banner */}
        {pricingInfo?.margin_applied && pricingInfo?.message && (
          <div className={`combined-guest-payment-header-banner hotel-detail-pricing-banner hotel-detail-pricing-banner--${pricingInfo.pricing_tier}`}>
            <span className="hotel-detail-pricing-banner__icon">
              {pricingInfo.pricing_tier === 'gt_plus' ? '✦' : '◈'}
            </span>
            <span className="hotel-detail-pricing-banner__pill">
              {pricingInfo.pricing_tier === 'gt_plus' ? 'GT Plus' : 'Exclusive'}
            </span>
            <span className="hotel-detail-pricing-banner__message">
              {pricingInfo.message}
            </span>
          </div>
        )}
      </div>

      <div className="combined-guest-payment-content">
        {/* Left Column: Guest Info + Payment */}
        <div className="combined-guest-payment-left">
          {/* Guest Information Section */}
          <div className="combined-guest-info-section">
            <h4>Your details</h4>
            <div className="combined-guest-info-banner">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 4V8M8 12H8.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>All fields marked with * are required and must be filled.</span>
            </div>

            <div className="combined-guest-info-fields">
              <div className="combined-guest-info-field">
                <label htmlFor="holder-first-name">First Name *</label>
                <input
                  id="holder-first-name"
                  type="text"
                  value={holder.firstName}
                  onChange={(e) => handleHolderChange('firstName', e.target.value)}
                  placeholder="First name"
                  required
                />
              </div>
              <div className="combined-guest-info-field">
                <label htmlFor="holder-last-name">Last Name *</label>
                <input
                  id="holder-last-name"
                  type="text"
                  value={holder.lastName}
                  onChange={(e) => handleHolderChange('lastName', e.target.value)}
                  placeholder="Last name"
                  required
                />
              </div>
              <div className="combined-guest-info-field">
                <label htmlFor="holder-email">Email *</label>
                <input
                  id="holder-email"
                  type="email"
                  value={holder.email}
                  onChange={(e) => handleHolderChange('email', e.target.value)}
                  placeholder="email@example.com"
                  required
                />
              </div>
            </div>

            <div className="combined-guest-info-section">
              <h4>Guests ({adults} {adults === 1 ? 'guest' : 'guests'})</h4>
              {guests.map((guest, index) => (
                <div key={index} className="combined-guest-info-guest">
                  <h5>Guest {index + 1}</h5>
                  <div className="combined-guest-info-fields">
                    <div className="combined-guest-info-field">
                      <label htmlFor={`guest-${index}-first-name`}>First Name *</label>
                      <input
                        id={`guest-${index}-first-name`}
                        type="text"
                        value={guest.firstName}
                        onChange={(e) => handleGuestChange(index, 'firstName', e.target.value)}
                        placeholder="First name"
                        required
                      />
                    </div>
                    <div className="combined-guest-info-field">
                      <label htmlFor={`guest-${index}-last-name`}>Last Name *</label>
                      <input
                        id={`guest-${index}-last-name`}
                        type="text"
                        value={guest.lastName}
                        onChange={(e) => handleGuestChange(index, 'lastName', e.target.value)}
                        placeholder="Last name"
                        required
                      />
                    </div>
                    <div className="combined-guest-info-field">
                      <label htmlFor={`guest-${index}-email`}>Email *</label>
                      <input
                        id={`guest-${index}-email`}
                        type="email"
                        value={guest.email}
                        onChange={(e) => handleGuestChange(index, 'email', e.target.value)}
                        placeholder="email@example.com"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Section - always visible */}
          <div className="combined-payment-section">
            <h4>Payment Information</h4>
            {paymentError && (
              <div className="combined-payment-error">
                <p>{paymentError}</p>
                <div className="combined-payment-error-actions">
                  {isPrebookError && (
                    <button
                      type="button"
                      className="combined-payment-error-try-again"
                      disabled={isPrebooking}
                      onClick={() => runPrebook()}
                    >
                      {isPrebooking ? 'Retrying…' : 'Try again'}
                    </button>
                  )}
                  <button type="button" className="combined-payment-error-back" onClick={onBack}>
                    ← Go back and start over
                  </button>
                </div>
              </div>
            )}

            <div className="combined-payment-container" style={{ position: 'relative' }}>
              {/* Payment container - SDK will render here once prebook is complete */}
              <div 
                ref={paymentContainerRef}
                id="payment-container-combined"
                style={{ 
                  minHeight: paymentError ? '0' : '400px',
                  width: '100%',
                  display: paymentError ? 'none' : undefined,
                }}
              />
              
              {/* Loading overlay - show while prebooking or loading payment SDK */}
              {(isPrebooking || (prebookComplete && isLoadingPayment)) && !paymentError && (
                <div className="combined-payment-loading" style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  zIndex: 10,
                  gap: '16px',
                }}>
                  <div className="combined-guest-payment-prebooking-spinner"></div>
                  <p>{isPrebooking ? 'Reserving your rate...' : 'Loading payment form...'}</p>
                  {isPrebooking && prebookStillWorking && (
                    <p className="combined-guest-payment-prebooking-note">Still working… This can take up to a minute.</p>
                  )}
                  {isPrebooking && !prebookStillWorking && (
                    <p className="combined-guest-payment-prebooking-note">This can take up to a minute</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Legal footer */}
          <div className="combined-payment-legal">
            <p>
              By continuing means you accept our{' '}
              <button onClick={() => setShowCancellationModal(true)} className="combined-payment-legal-link">
                Cancellation Policy
              </button>
              {' '}and{' '}
              <button onClick={() => setShowTermsModal(true)} className="combined-payment-legal-link">
                Terms
              </button>
              {' '}.
            </p>
          </div>
        </div>

        {/* Right Column: Room Details */}
        <div className="combined-guest-payment-right">
          <div className="combined-room-summary">
            {/* Hotel Image */}
            {hotel.images && hotel.images.length > 0 && (
              <div className="combined-room-summary-image" style={{ position: 'relative' }}>
                <Image src={hotel.images[0]} alt={hotel.name} fill style={{ objectFit: 'cover' }} />
              </div>
            )}

            {/* Hotel Info */}
            <div className="combined-room-summary-hotel">
              <h3>{hotel.name}</h3>
              {hotel.rating && (
                <div className="combined-room-summary-rating">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} className={i < Math.floor(hotel.rating || 0) ? 'filled' : ''}>★</span>
                  ))}
                </div>
              )}
              {hotel.address && (
                <p className="combined-room-summary-address">{hotel.address}</p>
              )}
            </div>

            {/* Check-in/Check-out */}
            <div className="combined-room-summary-dates">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="14" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M3 8.5H17" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M7 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M13 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>{formatDate(checkIn)} - {formatDate(checkOut)} ({nights} {nights === 1 ? 'night' : 'nights'})</span>
            </div>

            {/* Cancellation Policy */}
            <div className="combined-cancel-section">
              <div className="combined-cancel-section-header">
                <h4>Cancellation Policy</h4>
                <button
                  onClick={() => setShowCancellationModal(true)}
                  className="combined-room-summary-link"
                >
                  View details
                </button>
              </div>
              <p className="combined-cancel-section-sub">How much will it cost to cancel the rooms?</p>

              {cancellationPolicy && cancellationPolicy.tiers.length > 0 ? (
                <div className="combined-cancel-tiers">
                  {cancellationPolicy.tiers.map((tier, i) => {
                    const isFree = tier.amount === 0;
                    const isNoRefund = tier.amount >= totalBookingPrice;
                    const refundAmount = totalBookingPrice - tier.amount;
                    return (
                      <div key={i} className="combined-cancel-tier">
                        <div className="combined-cancel-tier-date">
                          <strong>{isFree ? 'Cancel by' : 'Cancel after'} {formatCancelDate(tier.cancelTime)}</strong>
                          <span>{formatCancelTime(tier.cancelTime)}</span>
                        </div>
                        <div className={`combined-cancel-tier-label${isFree ? ' free' : isNoRefund ? ' no-refund' : ' partial'}`}>
                          {isFree ? (
                            <>
                              <span>Free Cancellation</span>
                              <span>{formatPrice(totalBookingPrice, tier.currency)} (Full refund)</span>
                            </>
                          ) : isNoRefund ? (
                            <>
                              <span>{formatPrice(tier.amount, tier.currency)}</span>
                              <span>(No refund)</span>
                            </>
                          ) : (
                            <>
                              <span>{formatPrice(tier.amount, tier.currency)}</span>
                              <span>{formatPrice(refundAmount, tier.currency)} Refund</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <p className="combined-cancel-tiers-note">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1"/>
                      <path d="M7 3V7M7 10H7.01" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                    </svg>
                    Cancellation costs are based on the total booking value. All dates and times are mentioned in GMT.
                  </p>
                </div>
              ) : cancellationPolicy ? (
                <div className="combined-cancel-badge-row">
                  <span className={`combined-cancel-badge${cancellationPolicy.isRefundable ? ' refundable' : ' non-refundable'}`}>
                    {cancellationPolicy.isRefundable ? 'Refundable' : 'Non-Refundable'}
                  </span>
                  <p className="combined-cancel-simple-text">
                    {cancellationPolicy.isRefundable
                      ? 'This booking is refundable. Contact support for cancellation details.'
                      : 'This booking is non-refundable. You will not be refunded if you cancel.'}
                  </p>
                </div>
              ) : (
                <p className="combined-cancel-simple-text">See cancellation policy at the hotel.</p>
              )}
            </div>

            {/* Your Rooms */}
            <div className="combined-room-summary-rooms">
              <div className="combined-room-summary-rooms-header">
                <h4>Your Rooms</h4>
                <button 
                  onClick={() => setShowImportantInfoModal(true)}
                  className="combined-room-summary-link"
                >
                  Important Information
                </button>
              </div>

              <div className="combined-room-summary-room-item">
                <div className="combined-room-summary-room-header">
                  <span className="combined-room-summary-room-label">Room 1</span>
                  <button 
                    onClick={() => {
                      setRoomDetailsModalImageIndex(0);
                      setShowRoomDetailsModal(true);
                    }}
                    className="combined-room-summary-link"
                  >
                    Room details
                  </button>
                </div>
                <p className="combined-room-summary-room-type">
                  {selectedRate.roomName}, {selectedRate.breakfastIncluded ? 'Breakfast included' : 'No meals included'}...
                </p>
                <p className="combined-room-summary-room-price">
                  {formatPrice(calculatePaymentBreakdown.perNight, selectedRate.currency)} average per night
                </p>
              </div>

              {/* Price Breakdown */}
              <div className="combined-room-summary-price-breakdown">
                <div className="combined-room-summary-price-row">
                  <span>1 room x {nights} {nights === 1 ? 'night' : 'nights'}</span>
                  <span>{formatPrice(calculatePaymentBreakdown.roomBase, selectedRate.currency)}</span>
                </div>
                
                {calculatePaymentBreakdown.includedTaxes > 0 && (
                  <div className="combined-room-summary-price-row">
                    <span>
                      Included taxes and fees
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1"/>
                        <path d="M7 3V7M7 10H7.01" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                      </svg>
                    </span>
                    <span>{formatPrice(calculatePaymentBreakdown.includedTaxes, selectedRate.currency)}</span>
                  </div>
                )}
                
                {calculatePaymentBreakdown.localFees > 0 && (
                  <div className="combined-room-summary-price-row">
                    <span>
                      Local fees
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1"/>
                        <path d="M7 3V7M7 10H7.01" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                      </svg>
                    </span>
                    <span>{formatPrice(calculatePaymentBreakdown.localFees, selectedRate.currency)}</span>
                  </div>
                )}
                
                <div className="combined-room-summary-price-total">
                  <strong>Total</strong>
                  <strong>{formatPrice(calculatePaymentBreakdown.grandTotal, selectedRate.currency)}</strong>
                </div>
                
                {/* Payment split section */}
                <div className="combined-room-summary-payment-split">
                  <div className="combined-room-summary-price-row combined-room-summary-pay-now">
                    <span>💳 Pay now</span>
                    <strong>{formatPrice(calculatePaymentBreakdown.payNow, selectedRate.currency)}</strong>
                  </div>
                  
                  {calculatePaymentBreakdown.payAtProperty > 0 && (
                    <div className="combined-room-summary-price-row combined-room-summary-pay-later">
                      <span>💵 Pay at Property</span>
                      <strong>{formatPrice(calculatePaymentBreakdown.payAtProperty, selectedRate.currency)}</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancellation Policy Modal */}
      {showCancellationModal && (
        <div className="combined-cancel-modal-overlay" onClick={() => setShowCancellationModal(false)}>
          <div className="combined-cancel-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="cancel-modal-title">
            <div className="combined-cancel-modal-header">
              <h3 id="cancel-modal-title" className="combined-cancel-modal-title">Cancellation Policy</h3>
              <button className="combined-cancel-modal-close" onClick={() => setShowCancellationModal(false)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="combined-cancel-modal-content">
              {cancellationPolicy && cancellationPolicy.tiers.length > 0 ? (
                <>
                  <p className="combined-cancel-modal-sub">How much will it cost to cancel the rooms?</p>
                  <div className="combined-cancel-modal-tiers">
                    {cancellationPolicy.tiers.map((tier, i) => {
                      const isFree = tier.amount === 0;
                      const isNoRefund = tier.amount >= totalBookingPrice;
                      const refundAmount = totalBookingPrice - tier.amount;
                      return (
                        <div key={i} className="combined-cancel-modal-tier">
                          <div className="combined-cancel-modal-tier-date">
                            <strong>{isFree ? 'Cancel by' : 'Cancel after'} {formatCancelDate(tier.cancelTime)}</strong>
                            <span>{formatCancelTime(tier.cancelTime)}</span>
                          </div>
                          <div className={`combined-cancel-modal-tier-label${isFree ? ' free' : isNoRefund ? ' no-refund' : ' partial'}`}>
                            {isFree ? (
                              <>
                                <span className="combined-cancel-modal-tier-label-main free">Free Cancellation</span>
                                <span className="combined-cancel-modal-tier-label-sub free">{formatPrice(totalBookingPrice, tier.currency)} (Full refund)</span>
                              </>
                            ) : isNoRefund ? (
                              <>
                                <span className="combined-cancel-modal-tier-label-main">{formatPrice(tier.amount, tier.currency)}</span>
                                <span className="combined-cancel-modal-tier-label-sub">(No refund)</span>
                              </>
                            ) : (
                              <>
                                <span className="combined-cancel-modal-tier-label-main">{formatPrice(tier.amount, tier.currency)}</span>
                                <span className="combined-cancel-modal-tier-label-sub">{formatPrice(refundAmount, tier.currency)} Refund</span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="combined-cancel-modal-note">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1"/>
                      <path d="M7 3V7M7 10H7.01" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                    </svg>
                    Cancellation costs are based on the total booking value. All dates and times are mentioned in GMT.
                  </p>
                </>
              ) : cancellationPolicy ? (
                <div className="combined-cancel-modal-simple">
                  <span className={`combined-cancel-badge${cancellationPolicy.isRefundable ? ' refundable' : ' non-refundable'}`}>
                    {cancellationPolicy.isRefundable ? 'Refundable' : 'Non-Refundable'}
                  </span>
                  <p>
                    {cancellationPolicy.isRefundable
                      ? 'This booking is refundable. Please contact support for cancellation details.'
                      : 'This booking is non-refundable. If you choose to change or cancel this booking, you will not be refunded.'}
                  </p>
                </div>
              ) : (
                <p className="combined-cancel-modal-simple">Please check the cancellation policy directly with the hotel.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="combined-terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="combined-terms-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="combined-terms-modal-header">
              <h3 className="combined-terms-modal-title">Terms and conditions</h3>
              <button className="combined-terms-modal-close" onClick={() => setShowTermsModal(false)} aria-label="Close">×</button>
            </div>
            <div className="combined-terms-modal-content">
              <NuiteeTermsContent />
            </div>
          </div>
        </div>
      )}

      {/* Room Details Modal */}
      {showRoomDetailsModal && roomDetailsModalData && (
        <div className="hotel-detail-room-details-modal-overlay" onClick={() => setShowRoomDetailsModal(false)}>
          <div className="hotel-detail-room-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hotel-detail-room-details-modal-header">
              <h3 className="hotel-detail-room-details-modal-title">Room details</h3>
              <button
                className="hotel-detail-room-details-modal-close"
                onClick={() => setShowRoomDetailsModal(false)}
                aria-label="Close room details modal"
              >
                ×
              </button>
            </div>
            <div className="hotel-detail-room-details-modal-content">
              {/* Room Image Carousel */}
              {roomDetailsModalData.roomImages.length > 0 && (
                <div className="hotel-detail-room-details-modal-image-section">
                  <div className="hotel-detail-room-details-modal-carousel" style={{ position: 'relative' }}>
                    <Image
                      src={roomDetailsModalData.roomImages[roomDetailsModalImageIndex]}
                      alt={selectedRate.roomName}
                      className="hotel-detail-room-details-modal-image"
                      fill
                      style={{ objectFit: 'cover' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/images/placeholder-hotel.png';
                      }}
                    />
                    {roomDetailsModalData.roomImages.length > 1 && (
                      <>
                        <button
                          className="hotel-detail-room-details-modal-arrow hotel-detail-room-details-modal-arrow-left"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRoomDetailsModalImageIndex((prev) => (prev === 0 ? roomDetailsModalData.roomImages.length - 1 : prev - 1));
                          }}
                          aria-label="Previous image"
                        >
                          ‹
                        </button>
                        <button
                          className="hotel-detail-room-details-modal-arrow hotel-detail-room-details-modal-arrow-right"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRoomDetailsModalImageIndex((prev) => (prev === roomDetailsModalData.roomImages.length - 1 ? 0 : prev + 1));
                          }}
                          aria-label="Next image"
                        >
                          ›
                        </button>
                        <div className="hotel-detail-room-details-modal-counter">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2ZM8 3C10.7614 3 13 5.23858 13 8C13 10.7614 10.7614 13 8 13C5.23858 13 3 10.7614 3 8C3 5.23858 5.23858 3 8 3Z" fill="currentColor"/>
                            <path d="M8 4C8.55228 4 9 4.44772 9 5V7H11C11.5523 7 12 7.44772 12 8C12 8.55228 11.5523 9 11 9H9V11C9 11.5523 8.55228 12 8 12C7.44772 12 7 11.5523 7 11V9H5C4.44772 9 4 8.55228 4 8C4 7.44772 4.44772 7 5 7H7V5C7 4.44772 7.44772 4 8 4Z" fill="currentColor"/>
                          </svg>
                          <span>{roomDetailsModalImageIndex + 1}/{roomDetailsModalData.roomImages.length}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Room Type */}
              <h4 className="hotel-detail-room-details-modal-room-type">{selectedRate.roomName}</h4>

              {/* Key Metrics */}
              <div className="hotel-detail-room-details-modal-metrics">
                {roomDetailsModalData.roomSize && (
                  <div className="hotel-detail-room-details-modal-metric">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M3 7H17" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M7 4V7" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M13 4V7" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                    <span>{roomDetailsModalData.roomSize}</span>
                  </div>
                )}
                <div className="hotel-detail-room-details-modal-metric">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="7" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="13" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M3 15C3 12 5 10 7 10C7.5 10 7.75 10.25 8 10.5C8.25 10.25 8.5 10 9 10C11 10 13 12 13 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>Sleeps {roomDetailsModalData.occupancy}</span>
                </div>
              </div>

              {/* Room Description */}
              {roomDetailsModalData.roomDetailsData?.description && (
                <div className="hotel-detail-room-details-modal-section">
                  <p 
                    className="hotel-detail-room-details-modal-description"
                    dangerouslySetInnerHTML={{ __html: roomDetailsModalData.roomDetailsData.description }}
                  />
                </div>
              )}

              {/* Popular Amenities */}
              {roomDetailsModalData.popularAmenities.length > 0 && (
                <div className="hotel-detail-room-details-modal-section">
                  <h5 className="hotel-detail-room-details-modal-section-title">Popular amenities</h5>
                  <div className="hotel-detail-room-details-modal-amenities-grid">
                    {roomDetailsModalData.popularAmenities.map((amenity, idx) => (
                      <div key={idx} className="hotel-detail-room-details-modal-amenity">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M7 10L9 12L13 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* General Amenities */}
              {roomDetailsModalData.generalAmenities.length > 0 && (
                <div className="hotel-detail-room-details-modal-section">
                  <h5 className="hotel-detail-room-details-modal-section-title-with-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                      <rect x="11" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                      <rect x="3" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                      <rect x="11" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                    <span>General</span>
                  </h5>
                  <div className="hotel-detail-room-details-modal-amenities-list">
                    {roomDetailsModalData.generalAmenities.map((amenity, idx) => (
                      <div key={idx} className="hotel-detail-room-details-modal-amenity-item">{amenity}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Internet */}
              {roomDetailsModalData.internetAmenities.length > 0 && (
                <div className="hotel-detail-room-details-modal-section">
                  <h5 className="hotel-detail-room-details-modal-section-title-with-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8C5.5 5.5 8 4 10 4C12 4 14.5 5.5 17 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M5.5 11C7 9.5 8.5 8.5 10 8.5C11.5 8.5 13 9.5 14.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="10" cy="14" r="1.5" fill="currentColor"/>
                    </svg>
                    <span>Internet</span>
                  </h5>
                  <div className="hotel-detail-room-details-modal-amenities-list">
                    {roomDetailsModalData.internetAmenities.map((amenity, idx) => (
                      <div key={idx} className="hotel-detail-room-details-modal-amenity-item">{amenity}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Services */}
              {roomDetailsModalData.servicesAmenities.length > 0 && (
                <div className="hotel-detail-room-details-modal-section">
                  <h5 className="hotel-detail-room-details-modal-section-title-with-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 16L8 10L11 13L16 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="16" cy="6" r="2" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                    <span>Services</span>
                  </h5>
                  <div className="hotel-detail-room-details-modal-amenities-list">
                    {roomDetailsModalData.servicesAmenities.map((amenity, idx) => (
                      <div key={idx} className="hotel-detail-room-details-modal-amenity-item">{amenity}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bed Types */}
              {roomDetailsModalData.roomDetailsData?.bedTypes && roomDetailsModalData.roomDetailsData.bedTypes.length > 0 && (
                <div className="hotel-detail-room-details-modal-section">
                  <h5 className="hotel-detail-room-details-modal-section-title">Bed configuration</h5>
                  <div className="hotel-detail-room-details-modal-amenities-list">
                    {roomDetailsModalData.roomDetailsData.bedTypes.map((bedType, idx: number) => {
                      const bt = bedType as Record<string, unknown>;
                      const bedTypeLabel = typeof bedType === 'string' 
                        ? bedType 
                        : `${bt.quantity || bt.count || 1} ${bt.bedType || bt.name || 'Bed'}${bt.bedSize ? ` (${bt.bedSize})` : ''}`;
                      return (
                        <div key={idx} className="hotel-detail-room-details-modal-amenity-item">{bedTypeLabel}</div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Important Information Modal */}
      {showImportantInfoModal && (
        <div className="important-info-modal-overlay" onClick={() => setShowImportantInfoModal(false)}>
          <div className="important-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="important-info-modal-header">
              <h3 className="important-info-modal-title">Important Information</h3>
              <button
                className="important-info-modal-close"
                onClick={() => setShowImportantInfoModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="important-info-modal-content">
              {/* Resort Fee / Important Information */}
              {hotelDetails?.data?.hotelImportantInformation && (
                <div className="important-info-section">
                  <h4 className="important-info-section-title">Remarks: Resort Fee Information:</h4>
                  <p className="important-info-section-text">{hotelDetails.data.hotelImportantInformation}</p>
                </div>
              )}

              {/* Check-in Policy */}
              {hotelDetails?.data?.checkinCheckoutTimes && (
                <div className="important-info-section">
                  <h4 className="important-info-section-title">Hotel Check-In Policy:</h4>
                  <ul className="important-info-list">
                    {hotelDetails.data.checkinCheckoutTimes.checkin && (
                      <li>Check-in time: {hotelDetails.data.checkinCheckoutTimes.checkin}</li>
                    )}
                    {hotelDetails.data.checkinCheckoutTimes.checkout && (
                      <li>Check-out time: {hotelDetails.data.checkinCheckoutTimes.checkout}</li>
                    )}
                    {hotelDetails.data.checkinCheckoutTimes.checkinStart && (
                      <li>Earliest check-in: {hotelDetails.data.checkinCheckoutTimes.checkinStart}</li>
                    )}
                    {hotelDetails.data.checkinCheckoutTimes.checkinEnd && (
                      <li>Latest check-in: {hotelDetails.data.checkinCheckoutTimes.checkinEnd}</li>
                    )}
                  </ul>
                  <p className="important-info-note">A major credit card is required at check-in.</p>
                </div>
              )}

              {/* Additional Policies */}
              {hotelDetails?.data?.policies && hotelDetails.data.policies.length > 0 && (
                <div className="important-info-section">
                  <h4 className="important-info-section-title">Additional Policies:</h4>
                  {hotelDetails.data.policies.map((policy, idx) => (
                    <div key={idx} className="important-info-policy">
                      {policy.name && <h5 className="important-info-policy-name">{policy.name}</h5>}
                      {(policy.description || policy.text) && (
                        <p className="important-info-policy-text">{policy.description || policy.text}</p>
                      )}
                      {policy.child_allowed && (
                        <p className="important-info-policy-detail">Children: {policy.child_allowed}</p>
                      )}
                      {policy.pets_allowed && (
                        <p className="important-info-policy-detail">Pets: {policy.pets_allowed}</p>
                      )}
                      {policy.parking && (
                        <p className="important-info-policy-detail">Parking: {policy.parking}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Fallback if no info available */}
              {!hotelDetails?.data?.hotelImportantInformation && 
               !hotelDetails?.data?.checkinCheckoutTimes && 
               (!hotelDetails?.data?.policies || hotelDetails.data.policies.length === 0) && (
                <p className="important-info-empty">No additional information available for this property.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CombinedGuestPaymentForm;
